// FBOHeaderRoundedUV.jsx
import { memo, useMemo, useRef } from "react"
import * as THREE from "three"
import { RenderTexture, PerspectiveCamera, } from "@react-three/drei"
import { useThree } from "@react-three/fiber"
import { useBreakpoints } from "../../../hook/useBreakpoints"

function makeRoundedShape(w, h, r) {
  const rr = Math.min(r, w * 0.5, h * 0.5)
  const x = -w / 2, y = -h / 2
  const s = new THREE.Shape()
  s.moveTo(x + rr, y)
  s.lineTo(x + w - rr, y); s.quadraticCurveTo(x + w, y, x + w, y + rr)
  s.lineTo(x + w, y + h - rr); s.quadraticCurveTo(x + w, y + h, x + w - rr, y + h)
  s.lineTo(x + rr, y + h); s.quadraticCurveTo(x, y + h, x, y + h - rr)
  s.lineTo(x, y + rr); s.quadraticCurveTo(x, y, x + rr, y)
  return s
}

// UV plein cadre 0..1 basé sur positions (x,y)
function setUVFromPositions(geometry, w, h) {
  const pos = geometry.attributes.position
  const uv = new THREE.BufferAttribute(new Float32Array(pos.count * 2), 2)
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i)
    uv.setXY(i, (x + w / 2) / w, (y + h / 2) / h)
  }
  geometry.setAttribute('uv', uv)
}

function FBOHeader({
  children,
  size = [8, 5],        // utilisé si fitToView = false
  fitToView = true,     // ✅ rend la carte responsive au <View>
  inset = 0,            // marge interne en unités monde (ex: 0.2)
  radius = 0.5,
  smoothness = 32,
  quality = 1,          // multiplie la résolution du FBO (1 = 1:1)
  samples = 8,
  ...props
}) {
  const { viewport, gl } = useThree()         // dans un <View>, c'est le viewport DU view
  const camRef = useRef()
  const { isMobile } = useBreakpoints();

  // Taille finale (monde) = taille du <View> en monde, moins un éventuel inset
  const [w, h] = useMemo(() => {
    const baseW = fitToView ? viewport.width : size[0]
    const baseH = fitToView ? viewport.height : size[1]
    return [
      Math.max(0.001, baseW - inset * 2),
      Math.max(0.001, baseH - inset * 2)
    ]
  }, [fitToView, size, inset, viewport.width, viewport.height])

  // Géométrie arrondie + UV 0..1
  const geom = useMemo(() => {
    const g = new THREE.ShapeGeometry(makeRoundedShape(w, h, radius), smoothness)
    setUVFromPositions(g, w, h)
    return g
  }, [w, h, radius, smoothness])

  // Résolution du FBO = taille (monde) * pixels/monde * quality
  // viewport.factor = px par unité monde (dans CE view)
  const pxPerWorld = viewport.factor
  const rtW = Math.max(64, Math.round(w * pxPerWorld * quality))
  const rtH = Math.max(64, Math.round(h * pxPerWorld * quality))
  const aspect = w / h

  return (
    <mesh geometry={geom} {...props} onPointerMove={() => null}>
      <meshBasicMaterial toneMapped={false}>
        <RenderTexture
          attach="map"
          width={rtW}
          height={rtH}
          samples={samples}
          camera={camRef}
        >
          <PerspectiveCamera
            ref={camRef}
            position={isMobile ? [0, 0, 25] : [0, 0, 15]}
            fov={17.5}
            near={1}
            far={200}
            aspect={aspect}
            makeDefault
            onUpdate={(self) => self.updateProjectionMatrix()}
          />
          <color attach="background" args={["#141622"]} />
          {/* <ambientLight intensity={0.5} /> */}
          {/* <directionalLight
            position={[3.3, 1.0, 4.4]}
            castShadow
            intensity={4}
            shadow-radius={50}
            
          /> */}
          {children}
        </RenderTexture>
      </meshBasicMaterial>
    </mesh>
  )
}

export default memo(FBOHeader)
